import { useState, useCallback, useEffect } from 'react';

import {
    uploadPersonalDocument,
    getPersonalDocuments,
    getPersonalDocumentsByCategory,
    updatePersonalDocument,
    deletePersonalDocument,
    bulkUploadPersonalDocuments,
    downloadPersonalDocument,
    getDocumentCategories,
    validateDocumentFile
} from '../services/employeeDocumentAPI';

const useEmployeeDocuments = (employeeId) => {
    // **STATE MANAGEMENT**
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // **DOCUMENT CATEGORIES**
    const [categories] = useState(() => getDocumentCategories());

    // **LOAD ALL DOCUMENTS**
    const loadDocuments = useCallback(async () => {
        if (!employeeId) return;

        try {
            setLoading(true);
            const response = await getPersonalDocuments(employeeId);

            if (response.success) {
                setDocuments(response.data.documents || []);
            }
        } catch (error) {
            setError(error.message || 'Belgeler yüklenirken hata oluştu');
            console.error('Load documents error:', error);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    // **UPLOAD SINGLE DOCUMENT**
    const uploadDocument = async (file, category, notes = '') => {
        try {
            // Validate file
            validateDocumentFile(file);

            setUploading(true);
            setError('');

            const formData = new FormData();
            formData.append('document', file);
            formData.append('category', category);
            if (notes) formData.append('notes', notes);

            const response = await uploadPersonalDocument(employeeId, formData);

            if (response.success) {
                setSuccess('Belge başarıyla yüklendi');
                await loadDocuments(); // Refresh documents
                return { success: true, data: response.data };
            }
        } catch (error) {
            const errorMessage = error.message || 'Belge yüklenirken hata oluştu';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setUploading(false);
        }
    };

    // **BULK UPLOAD DOCUMENTS**
    const uploadMultipleDocuments = async (employeeId, filesWithCategories) => {
  
        try {
            setUploading(true);
            setError('');

            // Validate all files
            filesWithCategories.forEach(({ file }) => {
                validateDocumentFile(file);
            });

            console.log('✅ File validation geçti');

            const formData = new FormData();

            // ✅ Files with 'documents' field name (multer expects this)
            filesWithCategories.forEach(({ file }) => {
                formData.append('documents', file);
            });

            // ✅ Documents metadata as JSON string for req.body.documents
            const documentsMetadata = filesWithCategories.map(({ category, notes }) => ({
                category: category,
                notes: notes || `${category} belgesi`
            }));

            // ✅ Backend req.body.documents olarak alabilmesi için
            formData.append('documents', JSON.stringify(documentsMetadata));

            console.log('📤 FormData hazırlandı, API çağrısı yapılıyor...');

            const response = await bulkUploadPersonalDocuments(employeeId, formData);

            console.log('📥 API Response:', response);


            if (response.success) {
                setSuccess(`${filesWithCategories.length} belge başarıyla yüklendi`);
                await loadDocuments();
                return { success: true, data: response.data };
            }
        } catch (error) {
            const errorMessage = error.message || 'Belgeler yüklenirken hata oluştu';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setUploading(false);
        }
    };

    // **UPDATE DOCUMENT**
    const updateDocument = async (documentId, updateData) => {
        try {
            setLoading(true);
            const response = await updatePersonalDocument(employeeId, documentId, updateData);

            if (response.success) {
                setSuccess('Belge güncellendi');
                await loadDocuments();
                return { success: true };
            }
        } catch (error) {
            const errorMessage = error.message || 'Belge güncellenirken hata oluştu';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // **DELETE DOCUMENT**
    const deleteDocument = async (documentId, documentName) => {
        if (!window.confirm(`"${documentName}" belgesini silmek istediğinizden emin misiniz?`)) {
            return { success: false, cancelled: true };
        }

        try {
            setLoading(true);
            const response = await deletePersonalDocument(employeeId, documentId);

            if (response.success) {
                setSuccess('Belge silindi');
                await loadDocuments();
                return { success: true };
            }
        } catch (error) {
            const errorMessage = error.message || 'Belge silinirken hata oluştu';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // **DOWNLOAD DOCUMENT**
    const downloadDocument = async (document) => {
        try {
            await downloadPersonalDocument(document.url, document.originalName);
            setSuccess('Belge indirildi');
        } catch (error) {
            setError('Belge indirilirken hata oluştu');
        }
    };

    // **GET DOCUMENTS BY CATEGORY**
    const getDocumentsByCategory = useCallback((category) => {
        return documents.filter(doc => doc.category === category);
    }, [documents]);

    // **GET DOCUMENT STATS**
    const getDocumentStats = useCallback(() => {
        const stats = {
            total: documents.length,
            byCategory: {},
            requiredComplete: 0,
            requiredTotal: 0
        };

        categories.forEach(category => {
            const categoryDocs = getDocumentsByCategory(category.id);
            stats.byCategory[category.id] = {
                count: categoryDocs.length,
                required: category.required,
                complete: categoryDocs.length > 0
            };

            if (category.required) {
                stats.requiredTotal++;
                if (categoryDocs.length > 0) {
                    stats.requiredComplete++;
                }
            }
        });

        stats.completionPercentage = stats.requiredTotal > 0
            ? Math.round((stats.requiredComplete / stats.requiredTotal) * 100)
            : 100;

        return stats;
    }, [documents, categories, getDocumentsByCategory]);


    // **AUTO LOAD - employeeId değiştiğinde otomatik load**
    useEffect(() => {
        console.log('🔄 useEmployeeDocuments: employeeId değişti:', employeeId);
        if (employeeId) {
            console.log('📥 useEmployeeDocuments: Documents yükleniyor...');
            loadDocuments();
        } else {
            // employeeId yoksa state'i temizle
            setDocuments([]);
            setError('');
            setSuccess('');
        }
    }, [employeeId, loadDocuments]);

    // **CLEAR MESSAGES**
    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    // **RETURN HOOK INTERFACE**
    return {
        // State
        documents,
        loading,
        uploading,
        error,
        success,
        categories,

        // Actions
        loadDocuments,
        forceRefresh: loadDocuments,
        uploadDocument,
        uploadMultipleDocuments,
        updateDocument,
        deleteDocument,
        downloadDocument,

        // Getters
        getDocumentsByCategory,
        getDocumentStats,

        // Utils
        clearMessages
    };
};

export default useEmployeeDocuments;